import { Link, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { type FormEvent, useMemo, useState } from "react";
import { useRole } from "../../components/AppShell";
import { AttachIcon, BackIcon, KnowledgeIcon, PlusIcon, SearchIcon } from "../../components/icons";
import { Empty, ListSkeleton, useConfirm, useToast } from "../../components/ui";
import { ApiError } from "../../lib/api";
import { useDeleteKnowledge, useKnowledge, useKnowledgeList, useProducts, useSaveKnowledge } from "../../lib/queries";
import type { KnowledgeDoc, KnowledgeKind } from "../../lib/types";
import {
  extractText,
  KNOWLEDGE_FILE_ACCEPT,
  KNOWLEDGE_FILE_MAX,
  KNOWLEDGE_PDF_PICK_MAX,
  knowledgeFileType,
} from "./extract";
import { optimizePdf } from "./optimize-pdf";

const useCanEdit = () => {
  const role = useRole();
  return role === "owner" || role === "admin";
};

export const KIND_LABEL: Record<KnowledgeKind, string> = {
  warranty: "Garantía",
  shipping: "Envíos y entregas",
  hours: "Horarios",
  service: "Servicio técnico",
  faq: "Preguntas frecuentes",
  manual: "Manual",
  tutorial: "Tutorial",
  policy: "Política",
  other: "Otro",
};
const KINDS = Object.keys(KIND_LABEL) as KnowledgeKind[];

/** Topics customers ask about all the time: suggested until the company writes them. */
const SUGGESTED: KnowledgeKind[] = ["warranty", "shipping", "hours", "service", "faq"];

const PLACEHOLDER: Record<KnowledgeKind, string> = {
  warranty:
    "Cuánto dura la garantía de cada tipo de producto, qué cubre y qué no, qué necesita el cliente (factura, modelo, fotos) y cómo se tramita.",
  shipping: "A qué zonas envían, cuánto tarda, cuánto cuesta y cómo se coordina la entrega.",
  hours: "Horario de atención por WhatsApp y de cada tienda. Feriados y cierres especiales.",
  service: "Qué reparaciones hacen, dónde se entrega el producto, cuánto tarda la revisión y cuánto cuesta.",
  faq: "Pregunta: ¿Hacen factura electrónica?\nRespuesta: Sí, con el número de cédula del cliente.",
  manual: "Pega el texto del manual o sube el PDF y revisa lo que Orbis leyó.",
  tutorial: "Paso a paso, como se lo explicarías a un cliente.",
  policy: "Cambios, devoluciones, pagos u otra regla que la IA deba conocer.",
  other: "Escribe como se lo explicarías a un cliente.",
};

const asKind = (v: unknown): KnowledgeKind | undefined =>
  typeof v === "string" && (KINDS as string[]).includes(v) ? (v as KnowledgeKind) : undefined;

const kb = (bytes: number) =>
  bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;

// ---- List ------------------------------------------------------------------

export function KnowledgePage() {
  const [q, setQ] = useState("");
  const [kind, setKind] = useState("");
  const list = useKnowledgeList(q, kind);
  const canEdit = useCanEdit();
  const items = list.data?.items ?? [];
  const filtered = !!(q || kind);
  // Only from the unfiltered list, so a search does not make every topic look missing.
  const missing = filtered ? [] : SUGGESTED.filter((k) => !items.some((d) => d.kind === k));

  return (
    <div className="page">
      <div className="page-head">
        <h1>Conocimiento</h1>
        <label className="search-field">
          <SearchIcon />
          <input
            className="input"
            type="search"
            placeholder="Buscar en títulos y textos"
            aria-label="Buscar en el conocimiento"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </label>
        <select
          className="select products-category"
          aria-label="Tipo"
          value={kind}
          onChange={(e) => setKind(e.target.value)}
        >
          <option value="">Todos los tipos</option>
          {KINDS.map((k) => (
            <option key={k} value={k}>
              {KIND_LABEL[k]}
            </option>
          ))}
        </select>
        {canEdit && (
          <div className="products-actions">
            <Link className="btn btn-primary" to="/conocimiento/$docId" params={{ docId: "nuevo" }}>
              <PlusIcon />
              Nueva ficha
            </Link>
          </div>
        )}
      </div>
      <p className="muted kb-intro">
        La IA responde solo con lo que está aquí y en el Catálogo. Cada ficha es un tema: escríbela como se la
        explicarías a un cliente.
      </p>

      {canEdit && !list.isPending && missing.length > 0 && (
        <div className="kb-suggest">
          <span>Temas que los clientes preguntan seguido:</span>
          {missing.map((k) => (
            <Link
              key={k}
              className="btn btn-sm"
              to="/conocimiento/$docId"
              params={{ docId: "nuevo" }}
              search={{ tipo: k }}
            >
              <PlusIcon />
              {KIND_LABEL[k]}
            </Link>
          ))}
        </div>
      )}

      <div className="card">
        {list.isPending ? (
          <ListSkeleton />
        ) : items.length === 0 ? (
          <Empty title={filtered ? "Ninguna ficha coincide." : "Todavía no hay fichas."}>
            <span>
              {filtered
                ? "Prueba con otra palabra o tipo."
                : "Empieza por la garantía y los horarios: es lo que más preguntan los clientes."}
            </span>
          </Empty>
        ) : (
          <ul className="product-list">
            {items.map((d) => (
              <li key={d.id}>
                <Link to="/conocimiento/$docId" params={{ docId: d.id }} className="product-row">
                  <span className="product-thumb product-thumb-empty" aria-hidden="true">
                    <KnowledgeIcon />
                  </span>
                  <span className="product-main">
                    <span className="product-name" title={d.title}>
                      {d.title}
                    </span>
                    <span className="product-meta">
                      <span>{KIND_LABEL[d.kind]}</span>
                      {d.fileName && (
                        <span className="kb-file-name" title={d.fileName}>
                          <AttachIcon />
                          {d.fileName}
                        </span>
                      )}
                      {d.productIds.length > 0 && (
                        <span>{d.productIds.length === 1 ? "1 producto" : `${d.productIds.length} productos`}</span>
                      )}
                    </span>
                  </span>
                  <span className="product-flags">
                    {!d.visibleToAi && <span className="pill pill-closed">Oculta a la IA</span>}
                    {d.visibleToAi && d.indexStatus === "pending" && (
                      <span className="pill pill-closed">Preparando para la IA…</span>
                    )}
                    {d.visibleToAi && d.indexStatus === "error" && (
                      <span className="pill pill-warn">La IA aún no la puede usar</span>
                    )}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// ---- Detail / edit ---------------------------------------------------------

export function KnowledgeDocPage() {
  const { docId } = useParams({ from: "/app/conocimiento/$docId" });
  const { tipo } = useSearch({ from: "/app/conocimiento/$docId" });
  const isNew = docId === "nuevo";
  const doc = useKnowledge(isNew ? undefined : docId);
  if (!isNew && doc.isPending) return <div className="page">Cargando…</div>;
  if (!isNew && !doc.data) return <div className="page">Esa ficha ya no existe.</div>;
  return <KnowledgeForm key={doc.data?.id ?? `nuevo-${tipo ?? ""}`} doc={doc.data ?? null} kind={asKind(tipo)} />;
}

type Draft = Pick<KnowledgeDoc, "title" | "kind" | "body" | "productIds" | "visibleToAi">;

function KnowledgeForm({ doc, kind }: { doc: KnowledgeDoc | null; kind?: KnowledgeKind }) {
  const canEdit = useCanEdit();
  const save = useSaveKnowledge();
  const remove = useDeleteKnowledge();
  const products = useProducts("", "");
  const confirm = useConfirm();
  const toast = useToast();
  const navigate = useNavigate();
  const [d, setD] = useState<Draft>(() =>
    doc
      ? { title: doc.title, kind: doc.kind, body: doc.body, productIds: doc.productIds, visibleToAi: doc.visibleToAi }
      : {
          title: kind && kind !== "other" ? KIND_LABEL[kind] : "",
          kind: kind ?? "faq",
          body: "",
          productIds: [],
          visibleToAi: true,
        },
  );
  /** undefined: keep the current file; null: remove it; a File: upload it on save. */
  const [file, setFile] = useState<{ blob: File; type: string; before?: number } | null | undefined>(undefined);
  const [reading, setReading] = useState(false);
  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setD((prev) => ({ ...prev, [k]: v }));

  const byId = useMemo(() => new Map((products.data?.items ?? []).map((p) => [p.id, p])), [products.data]);
  const available = (products.data?.items ?? []).filter((p) => !d.productIds.includes(p.id));
  const currentFile = file === undefined && doc?.fileName ? doc : null;

  async function onFile(f: File | undefined) {
    if (!f) return;
    const type = knowledgeFileType(f);
    if (!type) return toast("Sube un PDF o un archivo de texto (.txt o .md).");
    const isPdf = type === "application/pdf";
    if (f.size > (isPdf ? KNOWLEDGE_PDF_PICK_MAX : KNOWLEDGE_FILE_MAX))
      return toast(`El archivo pesa más de ${isPdf ? 60 : 15} MB.`);
    setReading(true);
    try {
      const [text, optimized] = await Promise.all([extractText(f), isPdf ? optimizePdf(f) : null]);
      const blob = optimized?.file ?? f;
      if (blob.size > KNOWLEDGE_FILE_MAX) {
        toast(`Aun optimizado, el PDF pesa ${kb(blob.size)}. El máximo es 15 MB: divídelo en partes.`);
        return;
      }
      setFile({ blob, type, before: optimized && optimized.after < optimized.before ? optimized.before : undefined });
      // An empty title, or the one a suggested topic filled in, takes the file's name.
      if (!d.title.trim() || d.title === KIND_LABEL[d.kind]) set("title", f.name.replace(/\.[^.]+$/, "").slice(0, 200));
      if (!text) {
        toast("Este PDF no tiene texto que se pueda leer (parece escaneado). Escribe o pega el texto.");
        return;
      }
      const replace =
        !d.body.trim() ||
        (await confirm({
          title: "¿Usar el texto del archivo?",
          message: "Reemplaza el texto que está escrito ahora por el que Orbis leyó del archivo.",
          confirmLabel: "Reemplazar",
        }));
      if (replace) set("body", text.slice(0, 200_000));
    } catch {
      toast("No se pudo leer el archivo. Revisa que no esté protegido con contraseña.");
    } finally {
      setReading(false);
    }
  }

  async function removeFile() {
    const ok = await confirm({
      title: "¿Quitar el archivo original?",
      message: "El texto de la ficha se queda. Se borra el archivo al guardar.",
      confirmLabel: "Quitar",
    });
    if (ok) setFile(null);
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    try {
      const saved = await save.mutateAsync({ id: doc?.id, input: d, file });
      setFile(undefined);
      toast(doc ? "Ficha guardada" : "Ficha creada");
      if (!doc) navigate({ to: "/conocimiento/$docId", params: { docId: saved.id }, search: {} });
    } catch (err) {
      toast(err instanceof ApiError ? err.message : "No se pudo guardar.");
    }
  }

  async function onDelete() {
    if (!doc) return;
    const ok = await confirm({
      title: `¿Eliminar ${doc.title}?`,
      message: "La IA deja de usar esta información. Si tiene un archivo original, también se borra.",
      confirmLabel: "Eliminar",
    });
    if (!ok) return;
    await remove.mutateAsync(doc.id);
    toast("Ficha eliminada");
    navigate({ to: "/conocimiento" });
  }

  return (
    <div className="page">
      <Link className="back-link" to="/conocimiento">
        <BackIcon />
        Conocimiento
      </Link>
      <form className="product-form" onSubmit={onSubmit}>
        <fieldset disabled={!canEdit} className="product-grid">
          <div className="card product-col">
            <div className="card-body">
              <h3>Ficha</h3>
              <div className="two-cols">
                <div className="field">
                  <label htmlFor="kf-title">Título</label>
                  <input
                    className="input"
                    id="kf-title"
                    placeholder="Ej.: Manual de la plantilla PG-2V"
                    required
                    maxLength={200}
                    value={d.title}
                    onChange={(e) => set("title", e.target.value)}
                  />
                </div>
                <div className="field">
                  <label htmlFor="kf-kind">Tipo</label>
                  <select
                    className="select"
                    id="kf-kind"
                    value={d.kind}
                    onChange={(e) => set("kind", e.target.value as KnowledgeKind)}
                  >
                    {KINDS.map((k) => (
                      <option key={k} value={k}>
                        {KIND_LABEL[k]}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="field">
                <label htmlFor="kf-body">Texto</label>
                <textarea
                  className="textarea kb-body"
                  id="kf-body"
                  required
                  rows={18}
                  maxLength={200_000}
                  placeholder={PLACEHOLDER[d.kind]}
                  value={d.body}
                  onChange={(e) => set("body", e.target.value)}
                />
                <span className="faint kb-count">
                  {d.body.length.toLocaleString("es-CR")} caracteres · Esto es lo que la IA lee.
                </span>
              </div>
            </div>
          </div>

          <div className="product-col">
            <div className="card">
              <div className="card-body">
                <h3>Archivo original</h3>
                <p className="muted">
                  Sube el PDF del manual o de la política: Orbis copia su texto en la ficha para que lo revises, y
                  guarda el archivo para enviarlo cuando un cliente lo pida.
                </p>
                {file && (
                  <div className="kb-file">
                    <AttachIcon />
                    <span className="kb-file-name" title={file.blob.name}>
                      {file.blob.name}
                    </span>
                    <span className="faint">
                      {file.before
                        ? `Optimizado: ${kb(file.before)} → ${kb(file.blob.size)}. Se sube al guardar`
                        : "Se sube al guardar"}
                    </span>
                  </div>
                )}
                {currentFile && (
                  <div className="kb-file">
                    <AttachIcon />
                    <a
                      className="kb-file-name"
                      href={`/api/knowledge/${currentFile.id}/file`}
                      target="_blank"
                      rel="noreferrer"
                      title={currentFile.fileName ?? ""}
                    >
                      {currentFile.fileName}
                    </a>
                    {currentFile.fileSize != null && <span className="faint">{kb(currentFile.fileSize)}</span>}
                    {canEdit && (
                      <button className="btn btn-sm" type="button" onClick={removeFile}>
                        Quitar
                      </button>
                    )}
                  </div>
                )}
                {file === null && <p className="faint">El archivo se borra al guardar.</p>}
                {canEdit && (
                  <label className="btn btn-sm kb-upload">
                    {reading ? "Leyendo y optimizando…" : currentFile || file ? "Cambiar archivo" : "Subir PDF o texto"}
                    <input
                      type="file"
                      className="sr-only"
                      accept={KNOWLEDGE_FILE_ACCEPT}
                      disabled={reading}
                      onChange={(e) => {
                        onFile(e.target.files?.[0]);
                        e.target.value = "";
                      }}
                    />
                  </label>
                )}
              </div>
            </div>

            <div className="card">
              <div className="card-body">
                <h3>Productos</h3>
                <p className="muted">
                  Si la ficha es de productos concretos (su manual, una garantía especial), elígelos. Si no, aplica a
                  todos.
                </p>
                {d.productIds.length > 0 && (
                  <ul className="kb-products">
                    {d.productIds.map((id) => {
                      const p = byId.get(id);
                      return (
                        <li key={id}>
                          <span className="kb-product-name" title={p?.name}>
                            {p ? (p.model ? `${p.model} · ${p.name}` : p.name) : "Producto eliminado"}
                          </span>
                          {canEdit && (
                            <button
                              className="btn btn-sm"
                              type="button"
                              aria-label={`Quitar ${p?.name ?? "producto"}`}
                              onClick={() =>
                                set(
                                  "productIds",
                                  d.productIds.filter((x) => x !== id),
                                )
                              }
                            >
                              Quitar
                            </button>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
                {canEdit && available.length > 0 && (
                  <select
                    className="select"
                    aria-label="Agregar producto"
                    value=""
                    onChange={(e) => e.target.value && set("productIds", [...d.productIds, e.target.value])}
                  >
                    <option value="">Agregar producto…</option>
                    {available.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.model ? `${p.model} · ${p.name}` : p.name}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </div>

            <div className="card">
              <div className="card-body">
                <h3>Para la IA</h3>
                <label className="check">
                  <input
                    type="checkbox"
                    checked={d.visibleToAi}
                    onChange={(e) => set("visibleToAi", e.target.checked)}
                  />
                  La IA puede usar esta ficha
                </label>
                <p className="muted">Desmárcala mientras la escribes o si es solo para el equipo.</p>
              </div>
            </div>
          </div>
        </fieldset>
        {canEdit && (
          <div className="product-save">
            {doc && (
              <button className="btn btn-danger" type="button" onClick={onDelete}>
                Eliminar
              </button>
            )}
            <button
              className="btn btn-primary"
              type="submit"
              disabled={save.isPending || reading || !d.title.trim() || !d.body.trim()}
            >
              {save.isPending ? "Guardando…" : doc ? "Guardar cambios" : "Crear ficha"}
            </button>
          </div>
        )}
      </form>
    </div>
  );
}
